import { z } from 'zod'
import { topicDraftSchema } from './topic'

/**
 * Dry run (V1 demo, CLI-XXX) — a read-only preview of the articles a topic WOULD fetch, run on the
 * UNSAVED wizard draft before it is persisted. News only, deliberately: only news needs nothing
 * but query ids to pull (see the route). Kept in its own schema/route/lib/component family because
 * the feature is expected to be short-lived and must be trivial to remove.
 *
 * The request is the whole topic draft (the wizard posts exactly what it holds); the server reads
 * only `news` and `reasoningFilter` from it.
 */
export const dryRunRequestSchema = z.object({ draft: topicDraftSchema })
export type DryRunRequest = z.infer<typeof dryRunRequestSchema>

/**
 * The data sources the dry run previews. Query-based only: news, X, LinkedIn, podcasts all match on
 * query ids and need no subscription, so they share the catalogue/tangential model. Broadcast (needs
 * a subscription to resolve saved searches) and newsletter (matches senders, not queries) are out.
 */
export const dryRunSourceSchema = z.enum(['news', 'x', 'linkedin', 'podcast'])
export type DryRunSource = z.infer<typeof dryRunSourceSchema>

/**
 * One article's relevance verdict from the semantic service, present only when the topic's
 * reasoning filter is on AND carries a prompt. `decision` mirrors the service's KEEP/REMOVE;
 * `relevancy`/`confidence` are its 0–1 scores.
 */
export const dryRunRelevanceSchema = z.object({
  decision: z.enum(['KEEP', 'REMOVE']),
  relevancy: z.number(),
  confidence: z.number(),
})
export type DryRunRelevance = z.infer<typeof dryRunRelevanceSchema>

/** One previewed article, in the shape the overlay's card renders — a trimmed mirror of a report
 *  issue item's snapshot, plus the optional relevance verdict. */
export const dryRunArticleSchema = z.object({
  hitId: z.string(),
  /** Which data source surfaced this article. */
  source: dryRunSourceSchema,
  headline: z.string(),
  url: z.string(),
  outlet: z.string().nullable(),
  author: z.string().nullable(),
  handle: z.string().nullable(),
  /** ISO datetime, or null. */
  publishDate: z.string().nullable(),
  /** A hosted cover/media image URL when the source carries one; null otherwise. */
  imageUrl: z.string().nullable(),
  summary: z.string().nullable(),
  /** Snippet HTML with `<mark>` highlights, already sanitized by the data API layer. */
  snippets: z.array(z.string()),
  terms: z.array(z.string()),
  isOpinion: z.boolean(),
  reach: z.number().nullable(),
  dataSource: z.string(),
  relevance: dryRunRelevanceSchema.nullable(),
  /**
   * True when this article was surfaced by a *tangential* query — a brand-new (untracked) topic
   * query the app matched to the closest existing query to borrow coverage from. The article is
   * flagged, not the query: the query is fine, the match is approximate.
   */
  tangential: z.boolean(),
})
export type DryRunArticle = z.infer<typeof dryRunArticleSchema>

/** What the run explains about itself, so the overlay can be honest about what happened. */
export const dryRunMetaSchema = z.object({
  windowDays: z.number(),
  /** Whether the relevance (semantic) service was called. */
  scored: z.boolean(),
  /** The reasoning-filter mode in effect, or null when the filter is off. */
  mode: z.enum(['advise', 'filter']).nullable(),
  /** Articles dropped because they scored REMOVE under `filter` mode. */
  filteredOut: z.number(),
  /** Distinct positive news query ids derived from the draft. */
  queriesTotal: z.number(),
  /** Of those, how many had no rows in the warehouse. */
  queriesMissing: z.number(),
  /** How many inferred (LLM-substituted) query ids were used to stand in for missing ones. */
  inferredUsed: z.number(),
})
export type DryRunMeta = z.infer<typeof dryRunMetaSchema>

export const dryRunResponseSchema = z.object({
  articles: z.array(dryRunArticleSchema),
  meta: dryRunMetaSchema,
})
export type DryRunResponse = z.infer<typeof dryRunResponseSchema>
